import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { StoryElement } from '../types';
import { decodeHtmlEntities } from '../utils/html';
import {
  displaySummaryFor,
  hasSummaryContentFor,
  hasTopicContext,
} from '../utils/storySummaryHelpers';
import { formatAge } from '../utils/formatTime';
import { linkHost } from '../utils/linkHost';
import { domainSearchUrl, readerLinkUrl } from '../utils/storyLinks';
import { displayScore, scoreTone } from '../utils/scoreBreakdown';
import { CopyButton } from './CopyButton';
import { GatedMarker } from './GatedMarker';
import { MediaKindMarker } from './MediaKindMarker';
import { DiscussionSection } from './DiscussionSection';
import { ListPicker } from './ListPicker';
import { StoryRowActions } from './StoryRowActions';
import { CuratorOverrideModal } from './CuratorOverrideModal';
import { ScoreBreakdownModal } from './ScoreBreakdownModal';
import { UnlinkTopicModal } from './UnlinkTopicModal';
import { IconPencil, IconUnlink } from './icons';
import * as adminStoryRepository from '../repositories/adminStoryRepository';
import { getStoryAlternates, recordEvent } from '../repositories/storyRepository';
import type { UserEventContext } from '../repositories/storyRepository';
import type { CuratorOverrideActionType } from '../utils/curatorOverride';
import { safeHref } from '../utils/html';
import { topicContextSourceLinks } from '../utils/topicContextSources';

/**
 * Link the source domain to the tradsearch view (/search), filtered to that
 * domain and sorted by ingestion date — the HN-style "all submissions from this
 * domain" page. Filters on canonical_domain to match the tradsearch Site column.
 */
type OverrideModalState = {
  type: CuratorOverrideActionType;
  targetArticleId?: string;
  targetArticleTitle?: string;
  topic?: string;
} | null;

interface StoryRowProps {
  story: StoryElement;
  isAdmin?: boolean;
  /** Non-admin main feed / saved-search rows: a very small metadata surface. */
  compactReader?: boolean;
  isFocused?: boolean;
  rowRef?: (el: HTMLElement | null) => void;
  isExpanded: boolean;
  onFocusStory: (id: string) => void;
  onToggleExpanded: (id: string) => void;
  onStoryUpdate?: () => void;
  eventContext: UserEventContext;
}

export function StoryRow({
  story,
  isAdmin,
  compactReader = false,
  isFocused,
  rowRef,
  isExpanded,
  onFocusStory,
  onToggleExpanded,
  onStoryUpdate,
  eventContext,
}: StoryRowProps) {
  const [localSummary, setLocalSummary] = useState<string | null>(null);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [overrideModal, setOverrideModal] = useState<OverrideModalState>(null);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerPos, setListPickerPos] = useState<{ top: number; left: number } | null>(null);
  const listBtnRef = useRef<HTMLButtonElement>(null);
  const [unlinkConfirm, setUnlinkConfirm] = useState<{ articleIds: string[]; label: string } | null>(null);
  const [unlinkedIds, setUnlinkedIds] = useState<Set<string>>(new Set());

  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  // null = not fetched yet. Set once per row, so collapsing and reopening does
  // not re-query.
  const [fetchedAlts, setFetchedAlts] = useState<NonNullable<StoryElement['alternatives']> | null>(null);
  const [loadingAlts, setLoadingAlts] = useState(false);

  const displaySummary = displaySummaryFor(story, localSummary);
  const hasSummaryContent = hasSummaryContentFor(story, localSummary);
  // Alternates arrive inline on most surfaces. Fast Search ships none, so its
  // rows carry a `topic_mate_count` instead and we fetch the list on expand
  // (issue #837) -- counted rather than assumed from "has a topic", because
  // 11.6% of its rows have a topic with no displayable sibling behind it.
  const inlineAlts = (story.alternatives || []).filter((a) => !unlinkedIds.has(a.id));
  const alts = fetchedAlts !== null
    ? [...inlineAlts, ...fetchedAlts.filter((a) => !unlinkedIds.has(a.id))]
    : inlineAlts;
  const pendingMateCount = fetchedAlts === null ? (story.topic_mate_count || 0) : 0;
  // The server's count outlives the fetch. Hydration stops at the backend's
  // TOPIC_MATE_HYDRATE_LIMIT, so on a big cluster `alts` is a best-first slice
  // of `mateCount`, not all of it -- taking the larger keeps the topic label
  // from dropping from "(106)" to "(26)" the moment a reader expands the row.
  // Unlinking is the one thing that really shrinks the cluster, so it comes off
  // the server's number too.
  const mateCount = Math.max(0, (story.topic_mate_count || 0) - unlinkedIds.size);
  // The collapsed row labels a topic with its cluster size, which must survive
  // the alternates being deferred -- otherwise a row that has siblings reads as
  // if it has none until someone expands it.
  const clusterSize = Math.max(alts.length, mateCount);
  const hiddenMateCount = Math.max(0, mateCount - alts.length);
  const hasExpandableContent = hasSummaryContent
    || alts.length > 0
    || pendingMateCount > 0
    || Boolean(story.discussion);
  const titleUrl = readerLinkUrl(story);
  const hasDetailsContent = alts.length > 0
    || pendingMateCount > 0
    || Boolean(story.discussion)
    || hasTopicContext(story);
  // Summary follows the explicit expand toggle only. Revealing it on mere
  // keyboard focus made every row grow as the cursor passed over it.
  const showSummary = isExpanded && hasSummaryContent && !editingSummary;

  const handleTitleClick = (e: React.MouseEvent) => {
    onFocusStory(story.id);
    if (titleUrl) {
      recordEvent(story.id, 'click_external', eventContext);
    }
  };

  const handleToggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFocusStory(story.id);
    if (!isExpanded) {
      void recordEvent(story.id, 'show_summary', eventContext);
    }
    onToggleExpanded(story.id);
  };

  const handleExpandedClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a, button, input, textarea, select')) return;
    handleToggleExpanded(e);
  };

  const handleRowClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a, button, input, textarea, select')) {
      onFocusStory(story.id);
      return;
    }
    if (isFocused) {
      handleToggleExpanded(e);
      return;
    }
    onFocusStory(story.id);
  };

  // Deferred hydration for rows whose surface did not ship alternates. A
  // failure leaves the row exactly as it renders today -- no alternates -- so
  // it stays silent rather than pushing an error into a reading surface.
  //
  // Triggered by the row being open, not by the gesture that opened it: the
  // reader's primary expand gestures are keyboard (l / a / ArrowRight, in
  // useStoryKeyboardNav), which never reach this component's click handlers.
  // Hanging the fetch off one of those handlers left every keyboard-expanded
  // row showing an empty details panel under a topic count that promised
  // sources.
  const loadAlternatesIfNeeded = async () => {
    if (fetchedAlts !== null || loadingAlts || !(story.topic_mate_count || 0)) return;
    setLoadingAlts(true);
    try {
      setFetchedAlts(await getStoryAlternates(story.id));
    } catch {
      setFetchedAlts([]);
    } finally {
      setLoadingAlts(false);
    }
  };

  useEffect(() => {
    if (isExpanded) void loadAlternatesIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const handleEditSummary = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSummaryDraft(displaySummary);
    setEditingSummary(true);
  };

  const handleSaveSummary = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSavingSummary(true);
    try {
      await adminStoryRepository.updateStory(story.id, { description: summaryDraft });
      setLocalSummary(summaryDraft);
      setEditingSummary(false);
    } catch {
      // silently fail — admin can retry
    } finally {
      setSavingSummary(false);
    }
  };

  const handleCancelSummary = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSummary(false);
  };

  const score = displayScore(story);
  const tone = scoreTone(score);
  const scoreToneClass = tone ? ` score-${tone}` : '';

  const handleToggleListPicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (listPickerOpen) {
      setListPickerOpen(false);
      return;
    }
    const rect = listBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    setListPickerPos({
      top: rect.bottom,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
    });
    setListPickerOpen(true);
  };

  return (
    <article
      ref={rowRef}
      tabIndex={-1}
      className={`feed-item ${story.is_read ? 'read' : 'unread'}${isFocused ? ' keyboard-focused' : ''}${compactReader ? ' compact-reader' : ''}`}
      onClick={handleRowClick}
      aria-current={isFocused ? 'true' : undefined}
    >
      <div className="feed-item-line1">
        <h2 className="feed-item-title">
          <span className="feed-item-title-text">
            {titleUrl ? (
              <a
                href={safeHref(titleUrl)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTitleClick}
              >
                {decodeHtmlEntities(story.title) || '(Untitled)'}
              </a>
            ) : (
              decodeHtmlEntities(story.title) || '(Untitled)'
            )}
          </span>
          {/* Paywall guidance is useful to every reader, including the compact
              non-admin feed surface. */}
          <GatedMarker isGated={story.is_gated} />
          {!compactReader && <MediaKindMarker kind={story.media_kind} />}
        </h2>
        {!compactReader && (story.published_at || story.created_at) && (
          <span className="feed-item-age">
            {formatAge((story.published_at || story.created_at)!)}
          </span>
        )}
        {/* Rows with nothing to expand still reserve the chevron's width so the
            age stays on the same right edge as every other row. */}
        {!(hasExpandableContent && !editingSummary) && (
          <span className="feed-item-summary-toggle-spacer" aria-hidden="true" />
        )}
        {hasExpandableContent && !editingSummary && (
          <button
            className={`feed-item-summary-toggle${isExpanded ? ' expanded' : ''}`}
            onClick={handleToggleExpanded}
            aria-label={isExpanded ? 'Hide story details' : 'View story details'}
            title={isExpanded ? 'Hide details (o)' : 'View details (o)'}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>
      {/* Zero-height flex break: forces row 2 (score/icons/domain/badge/time) below the title on mobile. */}
      <span className="feed-item-row-break" aria-hidden="true" />
      <div className="feed-item-line2">
        <div className="feed-item-meta">
          {!compactReader && isAdmin && score != null && (
            <button
              className={`feed-item-base-score feed-item-title-score${scoreToneClass}`}
              onClick={(e) => { e.stopPropagation(); setScoreModalOpen(true); }}
              title="Click to see score breakdown"
            >
              {score.toFixed(1)}
            </button>
          )}
          <StoryRowActions
            story={story}
            isAdmin={isAdmin}
            listButtonRef={listBtnRef}
            listPickerOpen={listPickerOpen}
            onToggleListPicker={handleToggleListPicker}
            onStoryUpdate={onStoryUpdate}
          />
          {story.canonical_domain && <span className="feed-item-domain">
            {compactReader ? (
              <Link to={domainSearchUrl(story.canonical_domain)} className="feed-item-domain-link" target="_blank" rel="noopener noreferrer">{story.canonical_domain}</Link>
            ) : story.linked_url ? (
              <>{linkHost(story.linked_url)} <Link to={domainSearchUrl(story.canonical_domain)} className="feed-item-via feed-item-domain-link" target="_blank" rel="noopener noreferrer">via {story.canonical_domain}</Link></>
            ) : (
              <Link to={domainSearchUrl(story.canonical_domain)} className="feed-item-domain-link" target="_blank" rel="noopener noreferrer">{story.canonical_domain}</Link>
            )}
          </span>}
          {story.feed_name && (!story.canonical_domain || !compactReader) && story.feed_name !== story.canonical_domain && (
            <span className="feed-item-source">{story.feed_name}</span>
          )}
          {!compactReader && story.category && <span className="feed-item-tag">{story.category}</span>}
          {story.estimated_reading_minutes != null && <span className="feed-item-reading-time">{story.estimated_reading_minutes} min read</span>}
          {!compactReader && (story.topic || (isAdmin && clusterSize > 0)) && (
            <span className="feed-item-topic-wrap">
              {story.topic && (
                <button
                  className="feed-item-topic"
                  onClick={handleToggleExpanded}
                  title={isExpanded ? 'Collapse (l)' : 'Expand (l)'}
                >
                  <span className="feed-item-topic-text">{story.topic}</span>
                  {clusterSize > 0 && (
                    <span className="feed-item-topic-count">&nbsp;· ({clusterSize + 1})</span>
                  )}
                </button>
              )}
            </span>
          )}
        </div>
      </div>
      {/* Zero-height flex break: forces the expanded summary below row 2 on mobile. */}
      <span className="feed-item-row-break" aria-hidden="true" />
      {showSummary && (
        <div className="feed-item-expanded" onClick={handleExpandedClick}>
          <div className="feed-item-expanded-summary">
            <span className="feed-item-summary">{displaySummary}</span>
            <div className="feed-item-summary-actions">
              {isAdmin && (
                <button
                  className="feed-item-summary-edit-btn"
                  onClick={handleEditSummary}
                  title="Edit summary"
                >
                  <IconPencil size={11} />
                </button>
              )}
              <CopyButton text={displaySummary} size={11} />
            </div>
          </div>
        </div>
      )}
      {isExpanded && !editingSummary && (
        <div className="feed-item-expanded feed-item-details" onClick={handleExpandedClick}>
          {!hasDetailsContent && !loadingAlts && (
            <div className="feed-item-details-empty">No additional details available.</div>
          )}
          {loadingAlts && alts.length === 0 && (
            <div className="feed-item-details-empty">Loading other sources...</div>
          )}
          {hasTopicContext(story) && story.topic_context && story.topic_context.map((cat) => (
            <section className="feed-item-expanded-section" key={cat.name}>
              <div className="feed-item-section-header">{cat.name}</div>
              <ul className="feed-item-context-list">
                {cat.facts.map((fact, fi) => (
                  <li key={fi} className="feed-item-context-fact">
                    <span className="feed-item-context-text">{fact.text}</span>
                    {fact.sources.length > 0 && (
                      <span className="feed-item-context-sources" aria-label="Sources">
                        {topicContextSourceLinks(story, fact.sources).map((source, sourceIndex) => (
                          <span key={`${source.label}-${sourceIndex}`}>
                            {sourceIndex > 0 && ', '}
                            <a
                              href={safeHref(source.href)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.stopPropagation();
                                void recordEvent(story.id, 'click_external', eventContext);
                              }}
                            >
                              {source.label}
                            </a>
                          </span>
                        ))}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {story.discussion && (
            <DiscussionSection
              discussion={story.discussion}
              onLinkClick={() => void recordEvent(story.id, 'click_external', eventContext)}
            />
          )}
          {alts.length > 0 && (
            <section className="feed-item-expanded-section">
              <div className="feed-item-section-header">
                More on this topic
                {hiddenMateCount > 0 && (
                  <span className="feed-item-section-note">
                    {' '}— showing {alts.length} of {clusterSize}
                  </span>
                )}
              </div>
              <ul className="feed-item-alternates-list">
                {alts.map((alt) => (
                  <li key={alt.id}>
                    <a
                      href={safeHref(alt.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                        void recordEvent(alt.id, 'click_external', eventContext);
                      }}
                    >
                      {alt.title}
                    </a>
                    <GatedMarker isGated={alt.is_gated} />
                    <MediaKindMarker kind={alt.media_kind} />
                    <span className="feed-item-alternates-meta">
                      {alt.canonical_domain && (
                        <span className="feed-item-alternates-domain">{alt.canonical_domain}</span>
                      )}
                      {alt.published_at && (
                        <span className="feed-item-alternates-age">{formatAge(alt.published_at)}</span>
                      )}
                      {alt.estimated_reading_minutes != null && (
                        <span className="feed-item-alternates-reading">{alt.estimated_reading_minutes} min</span>
                      )}
                    </span>
                    {isAdmin && (
                      <button
                        className="curator-override-btn"
                        title="Unlink from topic"
                        onClick={(e) => { e.stopPropagation(); setUnlinkConfirm({ articleIds: [alt.id], label: alt.title }); }}
                      >
                        <IconUnlink size={10} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
      {isAdmin && editingSummary && (
        <div className="feed-item-summary-edit" onClick={(e) => e.stopPropagation()}>
          <textarea
            className="feed-item-summary-textarea"
            value={summaryDraft}
            onChange={(e) => setSummaryDraft(e.target.value)}
            rows={3}
            autoFocus
          />
          <div className="feed-item-summary-edit-actions">
            <button className="summary-save-btn" onClick={handleSaveSummary} disabled={savingSummary}>
              {savingSummary ? 'Saving…' : 'Save'}
            </button>
            <button className="summary-cancel-btn" onClick={handleCancelSummary} disabled={savingSummary}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {unlinkConfirm && (
        <UnlinkTopicModal
          articleIds={unlinkConfirm.articleIds}
          label={unlinkConfirm.label}
          topicName={story.topic}
          onClose={() => setUnlinkConfirm(null)}
          onUnlinked={(ids) => {
            setUnlinkedIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.add(id)); return next; });
            setUnlinkConfirm(null);
          }}
        />
      )}
      {overrideModal && (
        <CuratorOverrideModal
          overrideType={overrideModal.type}
          articleId={story.id}
          articleTitle={decodeHtmlEntities(story.title) || '(Untitled)'}
          targetArticleId={overrideModal.targetArticleId}
          targetArticleTitle={overrideModal.targetArticleTitle}
          currentTopic={overrideModal.topic}
          onClose={() => setOverrideModal(null)}
        />
      )}
      {scoreModalOpen && (
        <ScoreBreakdownModal
          story={story}
          onClose={() => setScoreModalOpen(false)}
        />
      )}
      {listPickerOpen && listPickerPos && createPortal(
        <div
          className="list-picker-container"
          style={{ position: 'fixed', top: listPickerPos.top, left: listPickerPos.left, zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ListPicker
            articleId={story.id}
            open={listPickerOpen}
            onClose={() => setListPickerOpen(false)}
            triggerRef={listBtnRef}
          />
        </div>,
        document.body,
      )}
    </article>
  );
}
